/**
 * Feito pelo agente netexperts
 * Módulo: AnalyticsObra — Tab A Receber (carteira / previsão / inadimplência)
 * Data: 2026-06-14
 */
import { useObraArea } from '../hooks/useObraAnalytics'
import { DETAIL_CONFIG } from '../config/areaAnalytics'
import { formatCurrency, formatNumber, formatPercent } from '../../../../utils/obraAnalytics'
import KpiCard from '../../../../Components/Analytics/KpiCard'
import MetricCard from '../components/MetricCard'
import DevedoresPanel from '../components/DevedoresPanel'
import AgingPanel from '../components/AgingPanel'
import Previsao12MesesPanel from '../components/Previsao12MesesPanel'
import DrillDownTable from '../components/DrillDownTable'
import TabState from '../components/TabState'

const formatRefMonth = (iso) => {
    if (!iso) return ''
    const [y, m] = iso.split('-')
    return `${m}/${y}`
}

const AReceberTab = ({ obraId, investidor, period }) => {
    const { data, loading, error } = useObraArea(obraId, 'a-receber', { inicio: period.inicio, fim: period.fim }, investidor)

    if (loading || error || !data) return <TabState loading={loading} error={error} />

    const c = data.cards
    const refMonthLabel = formatRefMonth(data.referencia || period.fim)
    const previsao12 = data.previsao12Meses || data.curvaVencimentos || []
    const devedores = data.devedores || []
    const aging = data.aging || []

    return (
        <div className="space-y-6">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                <MetricCard
                    title="A Receber"
                    value={c.total}
                    format={formatCurrency}
                    subtitle="Em atraso (todos) + em dia na janela"
                />
                <MetricCard
                    title="Em atraso"
                    value={c.vencido}
                    format={formatCurrency}
                    subtitle={`${formatNumber(c.qtd_devedores ?? 0)} contrato(s) · todos os vencidos`}
                />
                <MetricCard
                    title="Em dia (a vencer)"
                    value={c.a_vencer}
                    format={formatCurrency}
                    subtitle={`${formatPercent(c.pct_a_vencer ?? 0)} da carteira`}
                />
                <MetricCard
                    title="Previsão próximos 12m"
                    value={c.total_previsao_12m}
                    format={formatCurrency}
                    subtitle={`A partir de ${refMonthLabel || '—'}`}
                />
                <KpiCard title="Inadimplência" value={formatPercent(c.taxa_inadimplencia)} subtitle="Em atraso / total da carteira" />
                <KpiCard title="Parcelas em aberto" value={formatNumber(c.parcelas)} subtitle="Na janela (em dia + vencidos da janela)" />
                <KpiCard title="Contratos com saldo" value={formatNumber(c.contratos)} subtitle="Na janela analisada" />
                <KpiCard title="Ticket médio" value={formatCurrency(c.ticket_medio)} subtitle="Média por parcela" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <AgingPanel aging={aging} totalVencido={c.vencido} />
                <DevedoresPanel
                    devedores={devedores}
                    montanteTotal={c.vencido}
                    qtdDevedores={c.qtd_devedores ?? devedores.length}
                />
            </div>

            <Previsao12MesesPanel
                serie={previsao12}
                referencia={refMonthLabel}
                totalPrevisao={c.total_previsao_12m ?? previsao12.reduce((s, r) => s + Number(r.total || 0), 0)}
            />

            <DrillDownTable obraId={obraId} area="a-receber" investidor={investidor} config={DETAIL_CONFIG['a-receber']} period={period} />
        </div>
    )
}

export default AReceberTab
